/**
 * Admin-curated bundle products controller
 * MIT Wellness Theme
 */
(() => {
  const registerBundleProducts = () => {
    if (!window.Alpine || window.Alpine._bundleProductsRegistered) return;

    window.Alpine._bundleProductsRegistered = true;
    Alpine.data('xBundleProducts', (config = {}) => ({
      sectionId: config.sectionId || '',
      discountPercentage: Number(config.discountPercentage || 0),
      moneyFormat: config.moneyFormat || '${{amount}}',
      bundleTitle: config.bundleTitle || 'Build Your Bundle',
      discountLabel: config.discountLabel || 'You save',
      products: [],
      loading: false,
      errorMessage: '',

      init() {
        if (typeof config === 'string') {
          this.sectionId = config;
        }

        const configEl = document.getElementById(`bp-config-${this.sectionId}`);
        if (configEl) {
          try {
            const parsedConfig = JSON.parse(configEl.textContent || '{}');
            this.discountPercentage = Number(parsedConfig.discountPercentage || this.discountPercentage);
            this.moneyFormat = parsedConfig.moneyFormat || this.moneyFormat;
            this.bundleTitle = parsedConfig.bundleTitle || this.bundleTitle;
            this.discountLabel = parsedConfig.discountLabel || this.discountLabel;
          } catch (e) {
            console.error('Failed to parse bundle config data:', e);
          }
        }

        const dataEl = document.getElementById(`bp-data-${this.sectionId}`);
        if (dataEl) {
          try {
            this.products = JSON.parse(dataEl.textContent || '[]').filter(Boolean);
          } catch (e) {
            console.error('Failed to parse bundle products data:', e);
            this.products = [];
          }
        }
      },

      isVariantAvailable(index) {
        const product = this.products[index];
        return Boolean(product && product.selectedVariant && product.selectedVariant.available);
      },

      get bundleProductCount() {
        return this.products.length;
      },

      get hasBundleProducts() {
        return this.bundleProductCount > 0;
      },

      get allSelectedAvailable() {
        return this.hasBundleProducts && this.products.every((product, index) => this.isVariantAvailable(index));
      },

      selectVariant(productIndex, variantId) {
        const product = this.products[productIndex];
        if (!product || !product.variants) return;

        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;

        product.selectedVariant = variant;
        product.selectedVariantId = variant.id;
        product.price = variant.price;
        product.compareAtPrice = variant.compare_at_price || 0;
        product.featuredImage = variant.featured_image ? variant.featured_image.src : product.defaultImage;

        this.errorMessage = variant.available
          ? ''
          : 'That variant is sold out. Choose an in-stock variant before adding the bundle.';
      },

      formatMoney(cents) {
        if (window.Alpine && Alpine.store('xHelper') && typeof Alpine.store('xHelper').formatMoney === 'function') {
          return Alpine.store('xHelper').formatMoney(cents, this.moneyFormat);
        }

        return '$' + (Number(cents || 0) / 100).toFixed(2);
      },

      get originalSubtotal() {
        return this.products.reduce((sum, product) => {
          return sum + (product && product.selectedVariant ? Number(product.selectedVariant.price) : 0);
        }, 0);
      },

      get discountedSubtotal() {
        if (!this.discountPercentage) return this.originalSubtotal;

        const discount = Math.round(this.originalSubtotal * (this.discountPercentage / 100));
        return Math.max(0, this.originalSubtotal - discount);
      },

      get totalSavings() {
        return Math.max(0, this.originalSubtotal - this.discountedSubtotal);
      },

      get formattedOriginalSubtotal() {
        return this.formatMoney(this.originalSubtotal);
      },

      get formattedDiscountedSubtotal() {
        return this.formatMoney(this.discountedSubtotal);
      },

      get formattedSavings() {
        return this.formatMoney(this.totalSavings);
      },

      makeBundleProperties() {
        return {
          _bundle_id: `${this.sectionId}-${Date.now()}`,
          _bundle_title: this.bundleTitle,
          _bundle_product_count: String(this.bundleProductCount),
          _bundle_discount_display: this.discountPercentage ? `${this.discountPercentage}%` : ''
        };
      },

      buildCartItems() {
        const properties = this.makeBundleProperties();

        return this.products.map((product) => ({
          id: product.selectedVariant.id,
          quantity: 1,
          properties
        }));
      },

      async handleAddToCart() {
        if (!this.hasBundleProducts) {
          this.errorMessage = 'This bundle has no products configured.';
          return;
        }

        if (!this.allSelectedAvailable) {
          this.errorMessage = 'One or more bundle products is sold out. Choose in-stock variants before adding the bundle.';
          return;
        }

        this.loading = true;
        this.errorMessage = '';

        let releaseCart;
        try {
          releaseCart = await window.mwCart.acquire();

          let sectionsToRender = [];
          if (window.Alpine && Alpine.store('xCartHelper') && typeof Alpine.store('xCartHelper').getSectionsToRender === 'function') {
            sectionsToRender = Alpine.store('xCartHelper').getSectionsToRender().slice(0, 5).map((section) => section.id);
          }

          const payload = {
            items: this.buildCartItems()
          };

          if (sectionsToRender.length) {
            payload.sections = sectionsToRender;
          }

          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          const data = await response.json();

          if (!response.ok || data.status === 422 || data.status === '422' || data.errors) {
            const message = data.description || data.message || 'Unable to add this bundle to cart. Please try again.';
            this.errorMessage = message;
            document.dispatchEvent(new CustomEvent('eurus:product:add-failed', { detail: { errorMessage: message } }));
            return;
          }

          let cartData = null;
          try {
            const cartResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
              headers: { 'Accept': 'application/json' }
            });
            cartData = await cartResponse.json();
          } catch (cartErr) {
            console.warn('Unable to refresh cart count after bundle add:', cartErr);
          }

          if (window.Alpine && Alpine.store('xCartHelper')) {
            if (data.sections && typeof Alpine.store('xCartHelper').reRenderSections === 'function') {
              await Alpine.store('xCartHelper').reRenderSections(data.sections);
            } else if (Alpine.store('xMiniCart') && typeof Alpine.store('xMiniCart').reLoad === 'function') {
              Alpine.store('xMiniCart').reLoad();
            }

            if (cartData && typeof cartData.item_count === 'number') {
              Alpine.store('xCartHelper').currentItemCount = cartData.item_count;
            } else {
              const cartBubble = document.getElementById('cart-icon-bubble');
              if (cartBubble) {
                Alpine.store('xCartHelper').currentItemCount = parseInt(cartBubble.innerHTML, 10) || 0;
              }
            }
          }

          document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: data.items || data } }));
          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

          if (window.Alpine) {
            if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
              Alpine.store('xQuickView').show = false;
            }

            if (Alpine.store('xPopup') && typeof Alpine.store('xPopup').close === 'function') {
              Alpine.store('xPopup').close();
            }

            if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
              Alpine.store('xCartNoti').setItem(data);
            }

            if (Alpine.store('xMiniCart') && typeof Alpine.store('xMiniCart').openCart === 'function') {
              Alpine.store('xMiniCart').openCart();
            } else {
              document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
            }
          }
        } catch (err) {
          console.error('Cart add error:', err);
          this.errorMessage = 'A network error occurred while adding this bundle. Please try again.';
          document.dispatchEvent(new CustomEvent('eurus:product:add-failed', { detail: { errorMessage: this.errorMessage } }));
        } finally {
          releaseCart?.();
          this.loading = false;
        }
      }
    }));
  };

  document.addEventListener('alpine:init', registerBundleProducts);
  registerBundleProducts();
})();
